import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import az from "../locales/az";
import ru from "../locales/ru";
import en from "../locales/en";

const STORAGE_KEY = "broxe-lang";
const DEFAULT_LANG = "az";

const messages = { az, ru, en };

const LanguageContext = createContext(null);

function getNested(obj, path) {
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return cur;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "az" || saved === "ru" || saved === "en") return saved;
    } catch (e) {
      /* ignore */
    }
    return DEFAULT_LANG;
  });

  const setLang = useCallback((next) => {
    if (!messages[next]) return;
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const htmlLang = lang === "az" ? "az" : lang === "ru" ? "ru" : "en";
    document.documentElement.lang = htmlLang;
  }, [lang]);

  const t = useCallback(
    (path) => {
      const dict = messages[lang] || messages[DEFAULT_LANG];
      const val = getNested(dict, path);
      if (typeof val === "string" || Array.isArray(val)) return val;
      return path;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return ctx;
}
